import { useQueryClient } from "@tanstack/react-query";
import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import styled, { css, useTheme } from "styled-components";
import { MdAdd } from "@react-icons/all-files/md/MdAdd";
import { MdClose } from "@react-icons/all-files/md/MdClose";

import {
  useCheckServerTodo,
  useCreateServerTodo,
  useUpdateServerTodo,
  useDeleteServerTodo,
  useToggleServerTodoEnabled,
} from "@core/hooks/mutations/todo";
import { useServerTodos } from "@core/hooks/queries/todo";
import type { Friend } from "@core/types/friend";
import type { ServerName } from "@core/types/lostark";
import type { Weekday } from "@core/types/schedule";
import type { CustomTodoFrequency, ServerTodoItem, ServerTodoState } from "@core/types/todo";
import { WEEKDAYS } from "@core/constants";
import { getTodayWeekday } from "@core/utils";
import queryKeyGenerator from "@core/utils/queryKeyGenerator";

import Check, * as CheckStyledComponents from "@components/todo/TodoList/element/Check";

interface Props {
  servers: ServerName[];
  friend?: Friend;
  showAllWeekdays?: boolean;
}

interface DerivedServerTodo extends ServerTodoItem {
  serverName: ServerName;
  enabled: boolean;
  checked: boolean;
  isVisibleToday: boolean;
}

const weekdayLabelMap: Record<Weekday, string> = {
  MONDAY: "월",
  TUESDAY: "화",
  WEDNESDAY: "수",
  THURSDAY: "목",
  FRIDAY: "금",
  SATURDAY: "토",
  SUNDAY: "일",
};

const ServerTodos = ({ servers, friend, showAllWeekdays = false }: Props) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const friendUsername = friend?.friendUsername;
  const canView = friend ? friend.fromFriendSettings.showWeekTodo : true;
  const canToggle = friend ? friend.fromFriendSettings.setting : true;
  const canCheck = friend ? friend.fromFriendSettings.checkWeekTodo : true;
  const isOwnTodo = !friend;

  if (friend) {
    // eslint-disable-next-line no-console
    console.log("[ServerTodos] friend context", {
      friendUsername,
      nickName: friend.nickName,
      permissions: {
        canView,
        canToggle,
        canCheck,
      },
      fromFriendSettings: friend.fromFriendSettings,
      servers,
      showAllWeekdays,
    });
  }

  const uniqueServers = useMemo(() => {
    return Array.from(new Set(servers)) as ServerName[];
  }, [servers]);

  const todayWeekday = getTodayWeekday();

  const serverTodosQuery = useServerTodos(friendUsername, {
    enabled: canView && uniqueServers.length > 0,
  });

  const queryKey = queryKeyGenerator.getServerTodos(friendUsername);

  const toggleEnabledMutation = useToggleServerTodoEnabled({
    onSuccess: (overview) => {
      queryClient.setQueryData(queryKey, overview);
    },
    onError: () => {
      toast.error("서버 숙제 표시 설정을 변경하지 못했습니다.");
    },
  });

  const checkServerTodoMutation = useCheckServerTodo({
    onSuccess: (overview) => {
      queryClient.setQueryData(queryKey, overview);
    },
    onError: () => {
      toast.error("서버 숙제를 체크하지 못했습니다.");
    },
  });

  const deleteServerTodoMutation = useDeleteServerTodo({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success("서버 숙제가 삭제되었습니다.");
    },
    onError: () => {
      toast.error("서버 숙제 삭제에 실패했습니다.");
    },
  });

  const createServerTodoMutation = useCreateServerTodo({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success("커스텀 숙제가 추가되었습니다.");
      setShowAddForm(false);
      resetForm();
    },
    onError: () => {
      toast.error("커스텀 숙제 추가에 실패했습니다.");
    },
  });

  const updateServerTodoMutation = useUpdateServerTodo({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success("커스텀 숙제가 수정되었습니다.");
      setEditingTodoId(null);
      setEditingServerName(null);
    },
    onError: () => {
      toast.error("커스텀 숙제 수정에 실패했습니다.");
    },
  });

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTodoId, setEditingTodoId] = useState<number | null>(null);
  const [editingServerName, setEditingServerName] = useState<string | null>(null);
  const [formName, setFormName] = useState("");
  const [formWeekdays, setFormWeekdays] = useState<Weekday[]>([]);
  const [formServerAll, setFormServerAll] = useState(true);
  const [formServerNames, setFormServerNames] = useState<string[]>([]);

  const resetForm = () => {
    setFormName("");
    setFormWeekdays([]);
    setFormServerAll(true);
    setFormServerNames([]);
  };

  const handleToggleWeekday = (day: Weekday) => {
    setFormWeekdays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleEditStart = (todo: DerivedServerTodo) => {
    setEditingTodoId(todo.todoId);
    setEditingServerName(todo.serverName);
    setFormName(todo.contentName);
    setFormWeekdays(todo.visibleWeekdays);
    const sn = todo.serverNames ?? [];
    setFormServerAll(sn.length === 0);
    setFormServerNames(sn.length === 0 ? [] : [...sn]);
    setShowAddForm(false);
  };

  const handleEditSubmit = () => {
    if (!formName.trim() || !editingTodoId) return;
    updateServerTodoMutation.mutate({
      todoId: editingTodoId,
      contentName: formName.trim(),
      visibleWeekdays: formWeekdays.length > 0 ? formWeekdays : undefined,
      frequency: formWeekdays.length > 0 ? "WEEKLY" : "DAILY",
      serverNames: formServerAll ? [] : formServerNames,
    });
  };

  const handleCreateSubmit = () => {
    if (!formName.trim()) {
      toast.error("숙제 이름을 입력해주세요.");
      return;
    }
    createServerTodoMutation.mutate({
      contentName: formName.trim(),
      defaultEnabled: true,
      visibleWeekdays: formWeekdays.length > 0 ? formWeekdays : undefined,
      frequency: formWeekdays.length > 0 ? "WEEKLY" : "DAILY",
      custom: true,
      serverNames: formServerAll ? [] : formServerNames,
    });
  };

  const handleToggleServerName = (serverName: string) => {
    setFormServerNames((prev) =>
      prev.includes(serverName)
        ? prev.filter((s) => s !== serverName)
        : [...prev, serverName]
    );
  };

  if (!canView || uniqueServers.length === 0) {
    return null;
  }

  if (serverTodosQuery.isLoading) {
    return (
      <SectionWrapper>
        <PlaceholderBox>서버 공통 숙제를 불러오는 중...</PlaceholderBox>
      </SectionWrapper>
    );
  }

  const overview = serverTodosQuery.data;

  if (!overview && !isOwnTodo) {
    return null;
  }

  const todos = overview?.todos ?? [];
  const states = overview?.states ?? [];

  if (todos.length === 0 && !isOwnTodo) {
    return null;
  }

  const statesByKey = states.reduce<Record<string, ServerTodoState>>(
    (acc, state) => {
      acc[getStateKey(state.serverName, state.todoId)] = state;
      return acc;
    },
    {}
  );

  const sections = uniqueServers.map((serverName) => {
    const mapped = todos
      .filter((todo) => {
        // serverNames가 비어있으면 전체 서버에 표시
        const sn = todo.serverNames ?? [];
        return sn.length === 0 || sn.includes(serverName);
      })
      .map<DerivedServerTodo>((todo) => {
        const state = statesByKey[getStateKey(serverName, todo.todoId)];
        const enabled =
          state?.enabled !== undefined ? state.enabled : todo.defaultEnabled;
        const checked = state?.checked ?? false;

        return {
          ...todo,
          serverName,
          enabled,
          checked,
          isVisibleToday: todo.visibleWeekdays.includes(todayWeekday),
        };
      });

    const items = showAllWeekdays
      ? mapped
      : mapped.filter((item) => item.enabled && item.isVisibleToday);

    return {
      serverName,
      items,
    };
  });

  const isBusy =
    toggleEnabledMutation.isPending || checkServerTodoMutation.isPending;

  const handleToggle = (
    todoId: number,
    serverName: ServerName,
    enabled: boolean
  ) => {
    if (!canToggle) {
      toast.error("깐부 설정 권한이 없어 변경할 수 없습니다.");
      return;
    }

    toggleEnabledMutation.mutate({
      friendUsername,
      todoId,
      serverName,
      enabled,
    });
  };

  const handleCheck = async (todo: DerivedServerTodo, shouldCheck: boolean) => {
    if (!todo.enabled || (!showAllWeekdays && !todo.isVisibleToday)) {
      return;
    }

    if (!canCheck) {
      toast.error("깐부 체크 권한이 없어 완료 표시를 할 수 없습니다.");
      return;
    }

    if (!statesByKey[getStateKey(todo.serverName, todo.todoId)]) {
      try {
        await toggleEnabledMutation.mutateAsync({
          friendUsername,
          todoId: todo.todoId,
          serverName: todo.serverName,
          enabled: true,
        });
      } catch (error) {
        return;
      }
    }

    checkServerTodoMutation.mutate({
      friendUsername,
      todoId: todo.todoId,
      serverName: todo.serverName,
      checked: shouldCheck,
    });
  };

  const handleDeleteTodo = (todoId: number, todoName: string) => {
    if (window.confirm(`"${todoName}" 숙제를 삭제하시겠습니까?`)) {
      deleteServerTodoMutation.mutate({ todoId });
    }
  };

  const getFrequencyLabel = (frequency?: CustomTodoFrequency) => {
    if (!frequency) return null;
    return frequency === "DAILY" ? "매일" : "주간";
  };

  const renderServerForm = (mode: "create" | "edit") => (
    <AddFormWrapper>
      <FormField>
        <FormInput
          type="text"
          placeholder="숙제 이름"
          value={formName}
          onChange={(e) => setFormName(e.target.value)}
          maxLength={20}
        />
      </FormField>
      <FormField>
        <FormLabel>요일</FormLabel>
        <WeekdaySelector>
          {WEEKDAYS.map((day) => (
            <WeekdayChip
              key={day}
              type="button"
              $selected={formWeekdays.includes(day)}
              onClick={() => handleToggleWeekday(day)}
            >
              {weekdayLabelMap[day]}
            </WeekdayChip>
          ))}
        </WeekdaySelector>
      </FormField>
      {uniqueServers.length > 1 && (
        <FormField>
          <FormLabel>서버</FormLabel>
          <ServerSelector>
            <ServerChipButton
              type="button"
              $selected={formServerAll}
              onClick={() => {
                setFormServerAll(true);
                setFormServerNames([]);
              }}
            >
              전체
            </ServerChipButton>
            {uniqueServers.map((sv) => (
              <ServerChipButton
                key={sv}
                type="button"
                $selected={!formServerAll && formServerNames.includes(sv)}
                onClick={() => {
                  if (formServerAll) {
                    setFormServerAll(false);
                    setFormServerNames([sv]);
                  } else {
                    handleToggleServerName(sv);
                  }
                }}
              >
                {sv}
              </ServerChipButton>
            ))}
          </ServerSelector>
        </FormField>
      )}
      <FormActions>
        <FormActionButton
          type="button"
          $variant="primary"
          onClick={mode === "create" ? handleCreateSubmit : handleEditSubmit}
          disabled={
            mode === "create"
              ? createServerTodoMutation.isPending
              : updateServerTodoMutation.isPending
          }
        >
          저장
        </FormActionButton>
        <FormActionButton
          type="button"
          $variant="secondary"
          onClick={() => {
            if (mode === "create") {
              setShowAddForm(false);
            } else {
              setEditingTodoId(null);
              setEditingServerName(null);
            }
            resetForm();
          }}
        >
          취소
        </FormActionButton>
      </FormActions>
    </AddFormWrapper>
  );

  return (
    <SectionWrapper>
      {sections.map((section) => (
        <ServerBox key={section.serverName}>
          <ServerHeader>
            <ServerHeaderLeft>
              <ServerNameText>{section.serverName}</ServerNameText>
              <SubTitle>원정대 공통 숙제</SubTitle>
            </ServerHeaderLeft>
          </ServerHeader>

          {section.items.length === 0 ? (
            <EmptyRow>
              {showAllWeekdays
                ? "표시할 숙제가 없습니다."
                : "오늘 노출되는 숙제가 없습니다."}
            </EmptyRow>
          ) : (
            section.items.map((todo) => {
              const isDisabled = !todo.enabled;
              const isCustom = todo.custom === true;
              const frequencyLabel = getFrequencyLabel(todo.frequency);
              const todoServerNames = todo.serverNames ?? [];

              if (isCustom && editingTodoId === todo.todoId && editingServerName === section.serverName) {
                return (
                  <React.Fragment key={`${section.serverName}-${todo.todoId}`}>
                    {renderServerForm("edit")}
                  </React.Fragment>
                );
              }

              return (
                <Row key={`${section.serverName}-${todo.todoId}`}>
                  <Check
                    indicatorColor={isCustom ? theme.app.palette.purple[300] : theme.app.palette.blue[350]}
                    totalCount={1}
                    currentCount={todo.checked ? 1 : 0}
                    onClick={() => {
                      if (!isBusy) {
                        handleCheck(todo, !todo.checked);
                      }
                    }}
                    onRightClick={() => {
                      if (!isBusy) {
                        handleCheck(todo, !todo.checked);
                      }
                    }}
                  >
                    <TodoTitle>
                      <span>
                        {isCustom && isOwnTodo ? (
                          <EditNameButton
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditStart(todo);
                            }}
                          >
                            {todo.contentName}
                          </EditNameButton>
                        ) : (
                          todo.contentName
                        )}
                      </span>
                      {isCustom && frequencyLabel && (
                        <CustomBadge>{frequencyLabel}</CustomBadge>
                      )}
                      {todo.visibleWeekdays.length > 0 && (
                        <WeekdayBadge $isCustom={isCustom}>
                          {formatWeekdays(todo.visibleWeekdays)}
                        </WeekdayBadge>
                      )}
                      {isCustom && todoServerNames.length > 0 && (
                        <ServerBadge>{todoServerNames.join(", ")}</ServerBadge>
                      )}
                      {isDisabled && <StatusTag>미출력</StatusTag>}
                    </TodoTitle>
                  </Check>
                  <RowActions>
                    {isOwnTodo && isCustom && (
                      <DeleteButton
                        type="button"
                        onClick={() => handleDeleteTodo(todo.todoId, todo.contentName)}
                        disabled={deleteServerTodoMutation.isPending}
                        title="삭제"
                      >
                        <MdClose size={16} />
                      </DeleteButton>
                    )}
                    <ToggleButton
                      type="button"
                      onClick={() =>
                        !isBusy &&
                        handleToggle(todo.todoId, todo.serverName, !todo.enabled)
                      }
                      $enabled={todo.enabled}
                      disabled={isBusy || !canToggle}
                      aria-pressed={todo.enabled}
                    >
                      <ToggleHandle />
                    </ToggleButton>
                  </RowActions>
                </Row>
              );
            })
          )}
        </ServerBox>
      ))}

      {isOwnTodo && !showAddForm && (
        <AddButtonRow>
          <AddButton
            type="button"
            onClick={() => {
              setShowAddForm(true);
              setEditingTodoId(null);
              resetForm();
            }}
          >
            <MdAdd size={16} />
            커스텀 숙제 추가
          </AddButton>
        </AddButtonRow>
      )}

      {isOwnTodo && showAddForm && renderServerForm("create")}
    </SectionWrapper>
  );
};

export default ServerTodos;

const getStateKey = (serverName: ServerName, todoId: number) => {
  return `${serverName}-${todoId}`;
};

const formatWeekdays = (weekdays: Weekday[]) => {
  return weekdays.map((weekday) => weekdayLabelMap[weekday]).join("·");
};

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ServerBox = styled.div`
  border: 1px solid ${({ theme }) => theme.app.border};
  background: ${({ theme }) => theme.app.bg.white};
  border-radius: 8px;
  overflow: hidden;
`;

const ServerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.app.border};
  background: ${({ theme }) => theme.app.bg.gray2};
`;

const ServerHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ServerNameText = styled.span`
  font-weight: 700;
  font-size: 15px;
`;

const SubTitle = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.app.text.gray1};
`;

const EmptyRow = styled.div`
  padding: 16px;
  font-size: 13px;
  color: ${({ theme }) => theme.app.text.gray1};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 10px;
  border-top: 1px solid ${({ theme }) => theme.app.border};

  ${CheckStyledComponents.Wrapper} {
    flex: 1;
  }
`;

const TodoTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
`;

const WeekdayBadge = styled.span<{ $isCustom?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 999px;
  font-size: 11px;
  color: ${({ theme, $isCustom }) =>
    $isCustom ? theme.app.palette.purple[300] : theme.app.palette.blue[350]};
  background: ${({ theme, $isCustom }) =>
    $isCustom ? theme.app.palette.purple[50] : theme.app.palette.blue[50]};
`;

const StatusTag = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.app.text.gray1};
  border: 1px solid ${({ theme }) => theme.app.border};
  padding: 1px 6px;
  border-radius: 6px;
`;

const toggleBase = css<{ $enabled: boolean }>`
  position: relative;
  width: 44px;
  height: 22px;
  border-radius: 999px;
  border: none;
  background: ${({ theme, $enabled }) =>
    $enabled ? theme.app.palette.blue[350] : theme.app.border};
  cursor: pointer;
  transition: background 0.2s ease;
  padding: 0;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ToggleButton = styled.button<{ $enabled: boolean }>`
  ${toggleBase};
`;

const ToggleHandle = styled.span`
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ theme }) => theme.app.bg.white};
  transform: translateX(0);
  transition: transform 0.2s ease;

  ${ToggleButton}[aria-pressed="true"] & {
    transform: translateX(22px);
  }
`;

const PlaceholderBox = styled.div`
  border: 1px dashed ${({ theme }) => theme.app.border};
  border-radius: 8px;
  padding: 16px;
  font-size: 13px;
  color: ${({ theme }) => theme.app.text.gray1};
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EditNameButton = styled.button`
  all: unset;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.app.text.gray1};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.app.palette.red[100]};
    color: ${({ theme }) => theme.app.palette.red[450]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const CustomBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 999px;
  font-size: 11px;
  color: ${({ theme }) => theme.app.palette.purple[300]};
  background: ${({ theme }) => theme.app.palette.purple[50]};
`;

const AddButtonRow = styled.div`
  padding: 4px 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  border: 1px dashed ${({ theme }) => theme.app.border};
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.app.text.gray1};
  font-size: 13px;
  padding: 6px 12px;
  cursor: pointer;
  width: 100%;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.app.palette.purple[300]};
    color: ${({ theme }) => theme.app.palette.purple[300]};
  }
`;

const AddFormWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.app.border};
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: ${({ theme }) => theme.app.bg.gray2};
`;

const FormField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  min-width: 40px;
  color: ${({ theme }) => theme.app.text.dark2};
`;

const FormInput = styled.input`
  flex: 1;
  border: 1px solid ${({ theme }) => theme.app.border};
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  background: ${({ theme }) => theme.app.bg.white};
  color: ${({ theme }) => theme.app.text.dark1};
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.app.palette.purple[300]};
  }
`;

const WeekdaySelector = styled.div`
  display: flex;
  gap: 4px;
`;

const WeekdayChip = styled.button<{ $selected: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.app.palette.purple[300] : theme.app.border};
  background: ${({ theme, $selected }) =>
    $selected ? theme.app.palette.purple[300] : "transparent"};
  color: ${({ theme, $selected }) =>
    $selected ? "#fff" : theme.app.text.gray1};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  transition: all 0.15s ease;
`;



const FormActions = styled.div`
  display: flex;
  gap: 6px;
  justify-content: flex-end;
`;

const FormActionButton = styled.button<{ $variant: "primary" | "secondary" }>`
  border: none;
  border-radius: 6px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;

  background: ${({ theme, $variant }) =>
    $variant === "primary"
      ? theme.app.palette.purple[300]
      : theme.app.border};
  color: ${({ $variant }) => ($variant === "primary" ? "#fff" : "inherit")};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ServerSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const ServerChipButton = styled.button<{ $selected: boolean }>`
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.app.palette.purple[300] : theme.app.border};
  background: ${({ theme, $selected }) =>
    $selected ? theme.app.palette.purple[300] : "transparent"};
  color: ${({ theme, $selected }) =>
    $selected ? "#fff" : theme.app.text.gray1};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
`;

const ServerBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 999px;
  font-size: 11px;
  color: ${({ theme }) => theme.app.palette.green[350]};
  background: ${({ theme }) => theme.app.palette.green[50]};
`;
